import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

// Booking pages per tier (admin can override via GET /recruiter/me -> booking_url).
export const CALENDLY = {
  free: "https://calendly.com/meet-rpopro/rpopro-recruiter-meeting",
  pro: "https://calendly.com/meet-rpopro/rpopro-pro-recruiter",
  elite: "https://calendly.com/meet-rpopro/rpopro-elite-recruiter",
};

// Defaults shown until the server's entitlement config is live (server is the source of truth).
export const DEFAULT_ENT = {
  free: { included: 0, period: "none", duration_min: 0, priority: false },
  pro: { included: 1, period: "quarter", duration_min: 15, priority: false },
  elite: { included: 1, period: "month", duration_min: 30, priority: true },
};

export const TIER_BENEFITS = {
  free: ["AI Career Companion & Well-Being Coach", "Recruiter-written career guides", "Group recruiter webinars & live Q&A, when scheduled", "A free recruiter intro call to see how 1:1 support works"],
  pro: ["Everything in Free", "One 15-minute recruiter consultation every quarter", "Recruiter Q&A resource library"],
  elite: ["Everything in Pro", "One 30-minute 1:1 recruiter consultation every month", "Priority booking & priority recruiter support", "Resume & LinkedIn review", "Interview preparation & job-search strategy", "Career positioning & compensation guidance", "Follow-up recommendations after every session"],
};

// One line per tier for pricing cards and plan details (included in the plan price).
export const RECRUITER_PERK = {
  free: "Speak to a recruiter live: free intro call + group Q&A",
  pro: "Speak to a recruiter live: 15-min call every quarter",
  elite: "Speak to a recruiter live: 30-min 1:1 call every month",
};

export const PERIOD_LABEL = { month: "this month", quarter: "this quarter", none: "" };

export const fallbackMe = (tier) => {
  const e = DEFAULT_ENT[tier] || DEFAULT_ENT.free;
  return { tier, pending: true, allowance: { ...e, used: null, remaining: e.included || 0 }, booking_url: CALENDLY[tier] || CALENDLY.free, upcoming: [], past: [], resources: [], consent: {} };
};

export const useRecruiter = (tier) => {
  const [me, setMe] = useState(null);
  const load = useCallback(() => api.get("/recruiter/me").then((r) => setMe({ ...fallbackMe(tier), ...r.data, pending: false }))
    .catch((e) => setMe(e.response?.status === 404 ? fallbackMe(tier) : { ...fallbackMe(tier), error: true })), [tier]);
  useEffect(() => { load(); }, [load]);
  return [me, load, setMe];
};

export const calendlySrc = (url, user) => {
  const q = new URLSearchParams({ embed_domain: window.location.host, embed_type: "Inline", hide_gdpr_banner: "1", background_color: "0b0a10", text_color: "fafafa", primary_color: "8224e3", utm_source: "rpopro_web" });
  if (user?.name) q.set("name", user.name);
  if (user?.email) q.set("email", user.email);
  if (user?.user_id || user?.id) q.set("utm_content", user.user_id || user.id);
  return `${url}?${q}`;
};

export const isCalendlyBooked = (e) => /calendly\.com$/.test(new URL(e.origin || "http://x").hostname) && e.data?.event === "calendly.event_scheduled";
export const recordBooking = (payload) => api.post("/recruiter/bookings", { event_uri: payload?.event?.uri, invitee_uri: payload?.invitee?.uri, starts_at: payload?.event?.start_time || payload?.start_time || undefined, join_url: payload?.event?.location?.join_url || payload?.join_url || undefined }).then((r) => r.data);
export const savePrep = (body) => api.put("/recruiter/prep", body).then((r) => r.data);
export const upgradeInterest = (note) => api.post("/recruiter/upgrade-interest", { note }).then((r) => r.data);
export const rateSession = (id, rating, comment) => api.post(`/recruiter/appointments/${id}/rating`, { rating, comment }).then((r) => r.data);
